import { Person, decoratePerson, DecoratedPerson } from './person';
import { Contact } from './contact';
import { formatPhoneDisplay } from '@/lib/format-phone-display';

// Re-export Person and Contact for convenience
export type { Person, DecoratedPerson, Contact };

// Client slug pattern — labels generated by the API match this pattern (e.g. "ami-196d08b3").
// Kept anchor-free so it can be safely embedded in larger regexes (e.g. route matching).
const CLIENT_SLUG_PATTERN = 'ami-[a-z0-9]+';
const CLIENT_SLUG_REGEX = new RegExp(`^${CLIENT_SLUG_PATTERN}$`);
export const CLIENT_ID_PATTERN = `(?:\\d+|${CLIENT_SLUG_PATTERN})`;
export const isClientSlug = (id: string | undefined): boolean => !!id && CLIENT_SLUG_REGEX.test(id);

/**
 * Per-iCode prefill data served by the client API (`icode_prefills` key on the
 * client partial). The offline app derives the `field_prefills` entry
 * (`source`, `requires_confirmation`) from this; only the data the client
 * can't compute locally is carried here.
 */
export interface ICodePrefill {
  raw_value: unknown;
  date?: string; // ISO date the value was captured
  document_name?: string; // source document (assessment) name
}

// Define the Client type for use across the application
export interface Client {
  id: number;
  status: string;
  label: string; // Client slug from the API (e.g., "ami-196d08b3") - unencrypted for indexing and routing
  encrypted_label?: string; // Actual client name - encrypted (used when unlocked)
  external_id?: string | null;
  created_at: string;
  updated_at: string;
  // Nested person object from API
  person: Person;
  // Address object from API
  address: {
    address_line1?: string;
    address_line2?: string | null;
    city?: string;
    state?: string;
    county?: string | null;
    zip?: string;
  };
  // Contacts array from API
  contacts?: Contact[];
  // Core data snapshot from the API — a flat map of field paths (e.g. "person.first_name")
  // to their current values, used to prefill core data fields in new documents
  core_data_snapshot?: Record<string, unknown>;
  // Per-iCode prefill data from the API, keyed by iCode. Seeds PARC prefills
  // into new documents created offline (see icode_prefill_seed.ts).
  icode_prefills?: Record<string, ICodePrefill>;
  // Program and site associations from API
  program_ids?: number[];
  site_ids?: number[];
  // Local tracking fields (not from API)
  organization_id: number; // Organization scope for data isolation
  syncedOffline: boolean;
  last_viewed_at?: string; // Timestamp for when the client was last viewed in the app
  _locally_protected?: boolean; // Prevents stale-client cleanup while documents are pending sync
  eTag?: string; // ETag from last clients fetch for conditional requests
  lastModified?: string; // Last-Modified date from last clients fetch
}

/**
 * Decorator function that adds computed properties to a Client
 * Decorates the person object and provides convenient access to fullName and initials
 */
export function decorateClient<T extends Client>(client: T) {
  const decoratedPerson = decoratePerson(client.person, client.label);

  return {
    ...client,
    person: decoratedPerson,
    // Convenience accessors that delegate to decorated person
    get fullName() {
      return decoratedPerson.fullName;
    },
    get initials() {
      return decoratedPerson.initials;
    },
    get primaryPhone() {
      const phones = client.person?.phone_numbers;
      if (!phones || phones.length === 0) return '';
      const primary = phones.find(p => p.primary);
      return formatPhoneDisplay((primary || phones[0]).number);
    }
  };
}

export type DecoratedClient = ReturnType<typeof decorateClient<Client>>;
